import get from 'lodash/get';

import { IPublicConfig } from '@r-client/shared/util/core';

import { Reporter } from './reporter';

export type IgnoreFilter = NonNullable<Reporter.Configuration['checkIgnore']>;
type IgnoredMessage = string | RegExp;

const LOCALHOST_URLS = ['http://localhost', 'https://localhost'];

export const HOST_BLOCK_LIST = [
  'file:///',
  'chrome-extension://',
  'https://analytics.tiktok.com',
  ...LOCALHOST_URLS,
];

const URL_BLOCK_LIST = ['file:///', ...LOCALHOST_URLS];

const FILENAME_BLOCK_LIST = [
  'SyntaxError: Unexpected token',
  '(unknown)',
  'https://s.pinimg.com',
  'https://www.youtube.com/s/player/',
  // Safari private browsing / cross-origin scripts get their filename
  // rewritten to this opaque scheme, so the stack carries no actionable
  // information. Errors with every frame masked this way (e.g. ROLLBAR-6888
  // "Cannot call a class as a function" from /auth/forgot-password) are
  // almost always extension / 3p script noise we can never diagnose.
  // https://rollbar.com/Republic/r-client-republic/items/6888/
  'webkit-masked-url://',
  // Osano cookie-consent widget (cmp.osano.com) mutates the DOM concurrently
  // with React reconciliation and throws `NotFoundError` from inside React's
  // commit phase. Nothing in our bundle is on the throwing path.
  // https://rollbar.com/Republic/r-client-republic/items/789/
  'https://cmp.osano.com',
  // Zendesk legacy chat widget (static.zdassets.com) throws on its own
  // internals under many unrelated property names ('start', 'InboundFilters',
  // ...), which Rollbar's native grouping merges into one item. No app frame
  // is ever involved — confirmed across the sampled occurrences, all
  // single-frame and preceded by our own "Legacy Zendesk widget detected"
  // telemetry.
  // https://rollbar.com/Republic/r-client-republic/items/763/
  'https://static.zdassets.com',
];

// Errors that originate outside our application code: browser extensions,
// 3p script injection, iOS WebView bridge races, cross-origin iframes.
// They are not actionable client-side bugs and have been dominating the
// Rollbar inbox.
const MESSAGE_BLOCK_LIST_THIRD_PARTY: IgnoredMessage[] = [
  // Extension running eval() against the page.
  // https://rollbar.com/Republic/r-client-republic/items/1545/
  /call to eval\(\) blocked by CSP/,
  // Extension injection — global `output` not defined in page scope.
  // https://rollbar.com/Republic/r-client-republic/items/1838/
  /Can't find variable:? output/,
  // Extensions hitting the browser.storage quota.
  // https://rollbar.com/Republic/r-client-republic/items/3869/
  /browser\.storage\.local\.set\(\)\. Exceeded storage quota/,
  // Extension injecting a malformed script tag.
  // https://rollbar.com/Republic/r-client-republic/items/474/
  /Unexpected identifier 'https'/,
  // Browser extension port closed before response — chrome.runtime API noise.
  // https://rollbar.com/Republic/r-client-republic/items/4531/
  /Could not establish connection\. Receiving end does not exist/,
  // 3p iframe (cross-origin) trying to dispatch into ours.
  // https://rollbar.com/Republic/r-client-republic/items/6237/
  /Permission denied to access property .* on cross-origin object/,
  // iOS WebView bridge race — page renders before the native bridge is wired.
  // https://rollbar.com/Republic/r-client-republic/items/6970/
  /top\.webkit\.messageHandlers/,
  // Same iOS WebView race, earlier in the bridge bootstrap.
  // https://rollbar.com/Republic/r-client-republic/items/7026/
  /Cannot read property of undefined\/null expression window\.webkit/,
  // NOTE: generic JS parse errors (e.g. `SyntaxError: Unterminated string`)
  // are intentionally not blocked by message — they also fire when our own
  // bundle is truncated, and we don't want to hide those. 3p-injected variants
  // are filtered by source instead (isIgnoredByHost / isIgnoredByFilename).
  // Bad cookie / localStorage parse from injected code (not our JSON).
  // https://rollbar.com/Republic/r-client-republic/items/7064/
  /Expected property name or '\}' in JSON at position 1/,
  // Browser extension messaging its own background page after that page/tab
  // is gone — thrown from an injected content script, not our bundle. The
  // reported occurrences carry no frames at all, so message is the only lever.
  // https://rollbar.com/Republic/r-client-republic/items/838/
  // Reported as `Error: Invalid call to …`; matched on the bare fragment so
  // other prefixes of the same extension failure are covered too.
  /runtime\.sendMessage\(\)\. Tab not found/,
  // Benign browser notification surfaced as an error: the observer callback
  // resized its own observed element, so the browser defers a pass. Not a
  // defect, and unactionable — no stack frame of ours is involved.
  // https://rollbar.com/Republic/r-client-republic/items/1617/
  /ResizeObserver loop limit exceeded/,
  /ResizeObserver loop completed with undelivered notifications/,
];

const MESSAGE_BLOCK_LIST_ANY_ENV: IgnoredMessage[] = [
  /^GraphQL.+Operation (\w)+ failed with You are not (authorized|allowed) to perform this action/,
  ...MESSAGE_BLOCK_LIST_THIRD_PARTY,
];

// Expected backend rejections — authorization refusals and not-found domain
// errors. They are not frontend bugs, just business-logic outcomes that the
// UI already handles. Keep them in Rollbar at warning level (still queryable)
// so they stop polluting the error inbox. Real backend NPEs (e.g.
// `undefined method ... for nil`) are intentionally not matched here.
const DOWNGRADE_BACKEND_EXPECTED: RegExp[] = [
  // Backend "resource is protected" authz refusals.
  // https://rollbar.com/Republic/r-client-republic/items/3189/ (CreateDocument)
  // https://rollbar.com/Republic/r-client-republic/items/7001/ (CryptoCreateWalletDevice)
  /^GraphQL.+Operation \w+ failed with \w+ is protected/,
  // Backend domain not-found responses for expected lookups.
  // https://rollbar.com/Republic/r-client-republic/items/6515/
  // https://rollbar.com/Republic/r-client-republic/items/6516/
  /^GraphQL.+Operation \w+ failed with IssuerProfile not found/,
];

// `NotFoundError` raised when a DOM node was already detached or moved by
// someone else before React's commit phase touched it — the signature of a
// 3p script (cookie CMP, extension) reconciling the same subtree. Sampled
// occurrences carry react-dom frames only, so we cannot attribute them to a
// specific source and will not hard-ignore them: warning level keeps them
// queryable if one ever turns out to be ours. The engine-specific wordings
// below are verbatim reported messages except Gecko `insertBefore`, kept for symmetry.
// https://rollbar.com/Republic/r-client-republic/items/6662/
// https://rollbar.com/Republic/r-client-republic/items/6684/
// https://rollbar.com/Republic/r-client-republic/items/7495/
// https://rollbar.com/Republic/r-client-republic/items/7575/
// https://rollbar.com/Republic/r-client-republic/items/789/
const DOWNGRADE_DOM_RECONCILIATION_RACE: RegExp[] = [
  // Blink/WebKit-style DOMException wording.
  /Failed to execute '(removeChild|insertBefore)' on 'Node'/,
  // Gecko wording.
  /Node\.(removeChild|insertBefore): /,
  // Older WebKit collapses these to one opaque message.
  /The object can not be found here/,
];

// Known backend defect, not a frontend bug: the GraphQL error `message` we
// forward to Rollbar is the server's own `JSON.stringify` crash (a circular
// `issuerCertificate` chain), leaking through as if it were a domain error.
// Confirmed across 5 unrelated operations (getUser, GetFeatureToggle,
// GetOnboardingFlowState, GetUSOfferings, GlobalSearch), so the failing
// serialization sits in shared backend middleware, not a specific resolver.
// Kept at warning (not hard-ignored) because it is an unresolved backend bug.
// https://rollbar.com/Republic/r-client-republic/items/7390/
const DOWNGRADE_BACKEND_CIRCULAR_JSON: RegExp[] = [
  /^GraphQL.+Operation \w+ failed with Converting circular structure to JSON/,
];

const DOWNGRADE_TO_WARNING_PATTERNS: RegExp[] = [
  ...DOWNGRADE_BACKEND_EXPECTED,
  ...DOWNGRADE_DOM_RECONCILIATION_RACE,
  ...DOWNGRADE_BACKEND_CIRCULAR_JSON,
];

export const shouldDowngradeToWarning = (message: string): boolean => {
  return DOWNGRADE_TO_WARNING_PATTERNS.some((pattern) => pattern.test(message));
};

const MESSAGE_BLOCK_LIST_PRODUCTION: IgnoredMessage[] = [
  ...MESSAGE_BLOCK_LIST_ANY_ENV,
  /^Minified React error #418[^0-9].*/, // https://reactjs.org/docs/error-decoder.html?invariant=418
  /^Minified React error #423[^0-9].*/, // https://reactjs.org/docs/error-decoder.html?invariant=423
  /^Minified React error #425[^0-9].*/, // https://reactjs.org/docs/error-decoder.html?invariant=425
  // This is quite rare and is hard to debug, might happen when the user has connection issues
  // or just slow connection, reloads the page or closes the tab before the request is finished.
  /^The user aborted a request\./,
  /^Error: Invariant: attempted to hard navigate to the same URL.*/,
  /^TypeError: Failed to fetch/,
];
const MESSAGE_BLOCK_LIST_STAGING: IgnoredMessage[] = [
  ...MESSAGE_BLOCK_LIST_ANY_ENV,
];

export const getIgnoredMessages = (
  environment: IPublicConfig['applicationEnvironment']
): IgnoredMessage[] => {
  return environment === 'production'
    ? MESSAGE_BLOCK_LIST_PRODUCTION
    : MESSAGE_BLOCK_LIST_STAGING;
};

export const isIgnoredByHost: IgnoreFilter = (_isUncaught, _args, item) => {
  try {
    const url = get(item, 'request.url', '') as string;
    if (!url) return false;
    return URL_BLOCK_LIST.some((blockedHost) => url.startsWith(blockedHost));
  } catch {
    return false;
  }
};

type Frame = {
  filename?: string;
};

export const isIgnoredByFilename: IgnoreFilter = (_isUncaught, _args, item) => {
  try {
    const frames = get(item, 'body.trace.frames', []) as Frame[];
    return frames.some(
      ({ filename }) =>
        filename &&
        FILENAME_BLOCK_LIST.some((blockedFile) =>
          filename.startsWith(blockedFile)
        )
    );
  } catch {
    return false;
  }
};
